import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { Preloader } from "./components/Preloader";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { About } from "./components/About";
import { QuoteDivider } from "./components/QuoteDivider";
import { CtaBanner } from "./components/CtaBanner";
import { Courses } from "./components/Courses";
import { AnnouncementBar } from "./components/AnnouncementBar";
import { SpecialOffers } from "./components/SpecialOffers";
import { DualPaths } from "./components/DualPaths";
import { Consultations } from "./components/Consultations";
import { Manifesto } from "./components/Manifesto";
import { Testimonials } from "./components/Testimonials";
import { Faq } from "./components/Faq";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { WhatsAppFloat } from "./components/WhatsAppFloat";
import { AdminPage } from "./components/admin/AdminPage";
import AccountPage from "./pages/AccountPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import WatchPage from "./pages/WatchPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import MagicLogin from "./pages/MagicLogin";
import PublicPage from "./pages/PublicPage";
import LinksPage from "./pages/LinksPage";
import BlogPage from "./pages/BlogPage";
import PostPage from "./pages/PostPage";
import CoursePage from "./pages/CoursePage";
import JoinPage from "./pages/JoinPage";
import CorporatePage from "./pages/CorporatePage";
import { ClientsSection } from "./components/ClientsSection";
import { BlogSection } from "./components/BlogSection";
import { UpcomingBanners } from "./components/UpcomingBanners";
import { SiteContext, DEFAULT_SETTINGS, useSite, resolveMedia } from "./siteContext";
import { LangContext, STRINGS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_VARS = {
  bg: "--c-bg",
  bg_alt: "--c-bg-alt",
  surface: "--c-surface",
  accent: "--c-accent",
  accent_light: "--c-accent-light",
  text: "--c-text",
  muted: "--c-muted",
  mauve: "--c-mauve",
  light_bg: "--c-light-bg",
  light_text: "--c-light-text",
  light_muted: "--c-light-muted",
  light_border: "--c-light-border",
};

const SeoManager = ({ lang }) => {
  const settings = useSite();
  useEffect(() => {
    const pickSeo = (base) => (lang === "en" && settings[`${base}_en`] ? settings[`${base}_en`] : settings[base]);
    const title = pickSeo("seo_title");
    const setMeta = (attr, key, val) => {
      let m = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, key);
        document.head.appendChild(m);
      }
      m.setAttribute("content", val);
    };
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
    }
    const desc = pickSeo("seo_description");
    if (desc) {
      setMeta("name", "description", desc);
      setMeta("property", "og:description", desc);
    }
    const og = resolveMedia(settings.og_image);
    if (og) setMeta("property", "og:image", og);
  }, [settings, lang]);
  return null;
};

const Landing = () => {
  const settings = useSite();
  const hidden = settings.hidden_sections || [];
  const show = (id) => !hidden.includes(id);
  return (
    <>
      <Preloader />
      <AnnouncementBar />
      <Header />
      <main>
        <Hero />
        <DualPaths />
        {show("clients") && (<><ClientsSection /><QuoteDivider /></>)}
        {show("marquee") && <Marquee />}
        {show("about") && (<><About /><QuoteDivider /></>)}
        <SpecialOffers />
        {show("courses") && (<><Courses /><QuoteDivider /></>)}
        {show("consultations") && (<><Consultations /><QuoteDivider /></>)}
        {show("banners") && <UpcomingBanners />}
        {show("journey") && (<><Manifesto /><QuoteDivider /></>)}
        {show("testimonials") && (<><Testimonials /><QuoteDivider /></>)}
        {show("blog") && (<><BlogSection /><QuoteDivider /></>)}
        {show("faq") && (<><Faq /><QuoteDivider /></>)}
        {show("cta_banner") && <CtaBanner />}
        {show("contact") && <Contact />}
      </main>
      <Footer />
      {show("whatsapp_float") && <WhatsAppFloat />}
    </>
  );
};

function App() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [lang, setLangState] = useState(() => localStorage.getItem("themaya_lang") || "ar");

  const setLang = (l) => {
    localStorage.setItem("themaya_lang", l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.style.setProperty("--font-serif", lang === "ar" ? "Amiri" : "'Cormorant Garamond'");
  }, [lang]);

  useEffect(() => {
    const block = (e) => {
      if (e.target.closest && e.target.closest("input, textarea, select, [contenteditable='true']")) return;
      e.preventDefault();
    };
    document.addEventListener("contextmenu", block);
    document.addEventListener("dragstart", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("dragstart", block);
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) =>
        setSettings({
          ...DEFAULT_SETTINGS,
          ...res.data,
          colors: { ...DEFAULT_SETTINGS.colors, ...(res.data.colors || {}) },
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(COLOR_VARS).forEach(([key, cssVar]) => {
      if (settings.colors?.[key]) root.style.setProperty(cssVar, settings.colors[key]);
    });
    document.body.classList.toggle("bg-velvet", settings.bg_texture === "velvet");
    // Font control (admin Appearance tab)
    const fonts = settings.fonts || {};
    if (fonts.serif) root.style.setProperty("--font-serif", `'${fonts.serif}'`);
    if (fonts.sans) root.style.setProperty("--font-sans", `'${fonts.sans}'`);
    const fams = [...new Set([fonts.serif, fonts.sans].filter(Boolean))];
    if (fams.length) {
      const href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@300;400;600;700`).join("&")}&display=swap`;
      let link = document.getElementById("themaya-fonts");
      if (!link) {
        link = document.createElement("link");
        link.id = "themaya-fonts";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      if (link.href !== href) link.href = href;
    }
  }, [settings]);

  const rawOverrides = ((settings.texts || {})[lang]) || {};
  const cleanOverrides = Object.fromEntries(Object.entries(rawOverrides).filter(([, v]) => typeof v === "string" && v.trim() !== ""));
  const s = { ...STRINGS[lang], ...cleanOverrides };
  if ((settings.faqs || []).length) {
    s.faqs = settings.faqs.map((f) => ({ q: lang === "en" && f.q_en ? f.q_en : f.q, a: lang === "en" && f.a_en ? f.a_en : f.a }));
  }

  return (
    <LangContext.Provider value={{ lang, setLang, s }}>
      <SiteContext.Provider value={settings}>
        <div data-testid="themaya-app" className="bg-luxury-black min-h-screen text-luxury-bone">
          <div className="noise-overlay" />
          <SeoManager lang={lang} />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/account" element={<AccountPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/watch/:courseId" element={<WatchPage />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/access/:token" element={<MagicLogin />} />
              <Route path="/p/:slug" element={<PublicPage />} />
              <Route path="/links" element={<LinksPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<PostPage />} />
              <Route path="/course/:id" element={<CoursePage />} />
              <Route path="/join/:courseId" element={<JoinPage />} />
              <Route path="/corporate" element={<CorporatePage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </SiteContext.Provider>
    </LangContext.Provider>
  );
}

export default App;
